import { useState, useRef, useEffect, useCallback } from 'react';
import { API_BASE } from '../config';
import { hasEditAccess } from '../permissionConfig';
import { notifyEditBlocked } from '../components/EditAccessToast';
import { useTutorial } from '../components/tutorial/TutorialContext';
import { track } from '../trackActivity';
import { formatDate } from '../utils/formatDate';

const C = {
    white:    '#ffffff',
    ink:      '#111111',
    red:      '#D0202A',
    green:    '#158A45',
    greenSoft:'#EAFAF0',
    gray:     '#6b6b6b',
    line:     '#e6e6e6',
    panel:    '#f7f7f7',
};

const PREFIXES = ['26-27/PT', '26-27/IR'];
const MAX_IMAGE_WIDTH = 1280;

// Same compression pipeline as PoBoxImages.jsx — kept in sync with it.
function compressImage(file) {
    return new Promise((resolve) => {
        const reader = new FileReader();
        reader.onload = (e) => {
            const img = new Image();
            img.onload = () => {
                const canvas = document.createElement('canvas');
                let { width, height } = img;
                if (width > MAX_IMAGE_WIDTH) {
                    height = Math.round((height * MAX_IMAGE_WIDTH) / width);
                    width  = MAX_IMAGE_WIDTH;
                }
                canvas.width  = width;
                canvas.height = height;
                canvas.getContext('2d').drawImage(img, 0, 0, width, height);
                canvas.toBlob((blob) => resolve(blob), 'image/jpeg', 0.78);
            };
            img.src = e.target.result;
        };
        reader.readAsDataURL(file);
    });
}

// ── Date helpers — same small set SoVsInvoice.jsx/GRNManagement.jsx each
// duplicate locally (no shared date-utils module exists in this codebase). ──
function pad(n) { return String(n).padStart(2, '0'); }
function toISO(y, m, d) { return `${y}-${pad(m)}-${pad(d)}`; }
function todayISO() { const d = new Date(); return toISO(d.getFullYear(), d.getMonth() + 1, d.getDate()); }
function addDays(iso, days) {
    const d = new Date(iso + 'T00:00:00Z');
    d.setUTCDate(d.getUTCDate() + days);
    return toISO(d.getUTCFullYear(), d.getUTCMonth() + 1, d.getUTCDate());
}
function firstOfThisMonth() {
    const d = new Date();
    return toISO(d.getFullYear(), d.getMonth() + 1, 1);
}
function formatDateTime(value) {
    if (!value) return '—';
    const d = new Date(value);
    if (isNaN(d.getTime())) return '—';
    const h = d.getHours(), m = String(d.getMinutes()).padStart(2, '0');
    const h12 = ((h + 11) % 12) + 1;
    return `${formatDate(value)}, ${h12}:${m} ${h < 12 ? 'AM' : 'PM'}`;
}

const QUICK_RANGES = [
    { key: 'today',     label: 'Today' },
    { key: 'yesterday', label: 'Yesterday' },
    { key: '7d',        label: 'Last 7 days' },
    { key: 'month',     label: 'This month' },
];

export default function PodByHandUpload({ user, onLogout }) {
    const { startTour } = useTutorial();
    const [tab, setTab] = useState('upload'); // 'upload' | 'summary'

    const [prefix,       setPrefix]       = useState(PREFIXES[0]);
    const [manualMode,   setManualMode]   = useState(false);
    const [invoiceRest,  setInvoiceRest]  = useState('');
    const [manualInvoice,setManualInvoice]= useState('');
    const [suggestions,  setSuggestions]  = useState([]);
    const [matched,      setMatched]      = useState(null); // {invoice_no, order_ref_no, buyer}
    const [docketNo,     setDocketNo]     = useState('');
    const [photos,       setPhotos]       = useState([]); // [{file, preview, kind: 'image'|'pdf', name}]
    const [cameraOpen,   setCameraOpen]   = useState(false);
    const [saving,       setSaving]       = useState(false);
    const [toast,        setToast]        = useState('');
    const [error,        setError]        = useState('');

    // ── Summary tab state ────────────────────────────────────────────────
    const [fromDate,     setFromDate]     = useState(todayISO());
    const [toDate,       setToDate]       = useState(todayISO());
    const [quickRange,   setQuickRange]   = useState('today');
    const [summaryRows,  setSummaryRows]  = useState([]);
    const [summaryLoading, setSummaryLoading] = useState(false);
    const [summaryError, setSummaryError] = useState('');

    const fileInputRef       = useRef(null); // camera fallback (capture="environment")
    const deviceFileInputRef = useRef(null); // "Upload from device" — gallery/files, no capture
    const videoRef      = useRef(null);
    const streamRef     = useRef(null);
    const debounceRef   = useRef(null);

    useEffect(() => {
        return () => { streamRef.current?.getTracks().forEach(t => t.stop()); };
    }, []);

    const invoiceValue = manualMode ? manualInvoice.trim() : (invoiceRest.trim() ? prefix + invoiceRest.trim() : '');

    // ── Typeahead — debounced, best-effort only; final validation happens
    // server-side on save regardless of whether a suggestion was picked. ──
    useEffect(() => {
        setMatched(null);
        if (!invoiceValue || invoiceValue.length < 4) { setSuggestions([]); return; }
        clearTimeout(debounceRef.current);
        debounceRef.current = setTimeout(async () => {
            try {
                const res = await fetch(`${API_BASE}/pod-byhand-upload?action=search_invoice&q=${encodeURIComponent(invoiceValue)}`);
                const data = await res.json();
                setSuggestions(data.invoices || []);
            } catch {
                setSuggestions([]);
            }
        }, 250);
        return () => clearTimeout(debounceRef.current);
    }, [invoiceValue]);

    const showToast = (msg) => {
        setToast(msg);
        setTimeout(() => setToast(''), 3000);
    };

    function pickSuggestion(s) {
        setMatched(s);
        setSuggestions([]);
        if (manualMode) setManualInvoice(s.invoice_no);
        else {
            const rest = s.invoice_no.startsWith(prefix) ? s.invoice_no.slice(prefix.length) : s.invoice_no;
            setInvoiceRest(rest);
        }
    }

    // ── Live in-page camera, same pattern as PoBoxImages.jsx ────────────────
    async function openCamera() {
        if (!invoiceValue) { setError('Enter an invoice number first'); return; }
        if (!navigator.mediaDevices?.getUserMedia) {
            fileInputRef.current?.click();
            return;
        }
        try {
            const stream = await navigator.mediaDevices.getUserMedia({
                video: { facingMode: { ideal: 'environment' }, width: { ideal: 3840 }, height: { ideal: 2160 } },
                audio: false,
            });
            streamRef.current = stream;
            setCameraOpen(true);
        } catch (err) {
            console.error('getUserMedia failed, falling back to OS camera:', err);
            fileInputRef.current?.click();
        }
    }

    function closeCamera() {
        streamRef.current?.getTracks().forEach(t => t.stop());
        streamRef.current = null;
        setCameraOpen(false);
    }

    function attachVideoRef(el) {
        videoRef.current = el;
        if (el && streamRef.current && el.srcObject !== streamRef.current) {
            el.srcObject = streamRef.current;
            el.play().catch(err => console.error('Video play() failed:', err));
        }
    }

    function captureFrame() {
        const video = videoRef.current;
        if (!video || !video.videoWidth) return;
        const canvas = document.createElement('canvas');
        canvas.width  = video.videoWidth;
        canvas.height = video.videoHeight;
        canvas.getContext('2d').drawImage(video, 0, 0, canvas.width, canvas.height);
        canvas.toBlob(async (blob) => {
            if (!blob) return;
            const file = new File([blob], `capture_${Date.now()}.jpg`, { type: 'image/jpeg' });
            const compressed = await compressImage(file);
            setPhotos((prev) => [...prev, { file: compressed, preview: URL.createObjectURL(blob), kind: 'image', name: file.name }]);
        }, 'image/jpeg', 0.95);
    }

    async function handleFileInput(e) {
        const files = Array.from(e.target.files);
        if (!files.length) return;
        const compressed = await Promise.all(
            files.map(async (f) => ({ file: await compressImage(f), preview: URL.createObjectURL(f), kind: 'image', name: f.name }))
        );
        setPhotos((prev) => [...prev, ...compressed]);
        e.target.value = '';
    }

    // "Upload from Device" — an existing photo from the gallery, or a PDF
    // someone already has (e.g. a scanned docket), for whenever taking a
    // fresh photo isn't possible/convenient. PDFs pass through untouched
    // (compressImage only works on images); the backend merges them into
    // the final PDF page-for-page instead of embedding them as an image.
    async function handleDeviceFileInput(e) {
        const files = Array.from(e.target.files);
        if (!files.length) return;

        const staged = await Promise.all(files.map(async (f) => {
            if (f.type === 'application/pdf') {
                return { file: f, preview: null, kind: 'pdf', name: f.name };
            }
            return { file: await compressImage(f), preview: URL.createObjectURL(f), kind: 'image', name: f.name };
        }));
        setPhotos((prev) => [...prev, ...staged]);
        e.target.value = '';
    }

    const removePhoto = (idx) => setPhotos((prev) => prev.filter((_, i) => i !== idx));

    function resetForm() {
        setInvoiceRest('');
        setManualInvoice('');
        setManualMode(false);
        setSuggestions([]);
        setMatched(null);
        setDocketNo('');
        setPhotos([]);
        setError('');
    }

    async function handleSave() {
        if (!hasEditAccess(user, 'pod_upload', 'pod_upload_access')) return notifyEditBlocked();
        if (!invoiceValue) { setError('Enter an invoice number'); return; }
        if (photos.length === 0) { setError('Add at least one photo or document'); return; }

        setSaving(true);
        setError('');
        try {
            const formData = new FormData();
            formData.append('invoice_number', invoiceValue);
            if (docketNo.trim()) formData.append('docket_no', docketNo.trim());
            formData.append('created_by', user?.display_name || user?.username || '');
            photos.forEach((p, i) => formData.append('photos', p.file, p.kind === 'pdf' ? `pod_${i + 1}.pdf` : `pod_${i + 1}.jpg`));

            const res = await fetch(`${API_BASE}/pod-byhand-upload?action=save`, { method: 'POST', body: formData });
            const data = await res.json();
            if (!res.ok) throw new Error(data.error || 'Save failed');

            showToast(`✓ POD saved for ${invoiceValue}`);
            track(user?.username, 'UPLOAD', 'POD By Hand Upload', { invoice_number: invoiceValue });
            resetForm();
        } catch (err) {
            setError(err.message);
        }
        setSaving(false);
    }

    // ── Summary tab ──────────────────────────────────────────────────────
    const loadSummary = useCallback(async () => {
        setSummaryLoading(true);
        setSummaryError('');
        try {
            const qs = new URLSearchParams({ action: 'summary', date_from: fromDate, date_to: toDate });
            const res = await fetch(`${API_BASE}/pod-byhand-upload?${qs.toString()}`);
            const data = await res.json();
            if (!res.ok) throw new Error(data.error || 'Failed to load summary');
            setSummaryRows(data.rows || []);
        } catch (err) {
            setSummaryError(err.message);
        }
        setSummaryLoading(false);
    }, [fromDate, toDate]);

    // Fetches only once the Summary tab is actually opened (and again on any
    // date-range change while it's open) — no network call while someone's
    // just using the Upload tab, which is this page's primary action.
    useEffect(() => {
        if (tab === 'summary') loadSummary();
    }, [tab, loadSummary]);

    function applyQuickRange(key) {
        setQuickRange(key);
        if (key === 'today') { setFromDate(todayISO()); setToDate(todayISO()); }
        else if (key === 'yesterday') { const y = addDays(todayISO(), -1); setFromDate(y); setToDate(y); }
        else if (key === '7d') { setFromDate(addDays(todayISO(), -6)); setToDate(todayISO()); }
        else if (key === 'month') { setFromDate(firstOfThisMonth()); setToDate(todayISO()); }
    }
    function onManualDateChange(setter, value) {
        setter(value);
        setQuickRange(null);
    }

    function downloadSummaryExcel() {
        if (!window.XLSX || summaryRows.length === 0) return;
        const data = summaryRows.map((r, i) => ({
            '#': i + 1,
            'POD Date': formatDate(r.uploading_date),
            'Uploaded Date': formatDateTime(r.created_at),
            'Invoice Number': r.invoice_number,
            'Docket Number': r.docket_no || '—',
        }));
        // view_url deliberately excluded — it's a 1-hour SAS link, meaningless
        // in a spreadsheet opened days (or months) later.
        const ws = window.XLSX.utils.json_to_sheet(data);
        ws['!cols'] = [{ wch: 5 }, { wch: 14 }, { wch: 18 }, { wch: 18 }, { wch: 16 }];
        const wb = window.XLSX.utils.book_new();
        window.XLSX.utils.book_append_sheet(wb, ws, 'POD Summary');
        window.XLSX.writeFile(wb, `POD_Summary_${fromDate}_to_${toDate}.xlsx`);
    }

    // Clicking "View" opens the merged POD PDF in a new browser tab — same
    // convention every other "view a stored PDF" link in this app already
    // uses (SoVsInvoice.jsx, GRNManagement.jsx), rather than an in-page
    // viewer.
    function openPod(row) {
        if (!row.view_url) { showToast('This POD file is unavailable right now.'); return; }
        window.open(row.view_url, '_blank', 'noopener');
    }

    // ── Full-screen camera overlay ──────────────────────────────────────────
    if (cameraOpen) return (
        <div style={{ position:'fixed', inset:0, background:'#000', zIndex:300, display:'flex', flexDirection:'column', overflow:'hidden' }}>
            <video ref={attachVideoRef} autoPlay playsInline muted
                style={{ flex:'1 1 0', minHeight:0, minWidth:0, width:'100%', objectFit:'contain', background:'#000' }} />
            <div style={{ display:'flex', alignItems:'center', justifyContent:'center', gap:'28px', padding:'20px 16px', background:'#111' }}>
                <button onClick={closeCamera}
                    style={{ background:'#444', color:'#fff', border:'none', borderRadius:'10px', padding:'12px 20px', fontWeight:700, fontSize:'14px', cursor:'pointer' }}>
                    Done
                </button>
                <button onClick={captureFrame}
                    style={{ width:'68px', height:'68px', borderRadius:'50%', background:C.red, border:'3px solid #fff', cursor:'pointer' }} />
                <div style={{ width:'68px', textAlign:'center', color:'#fff', fontSize:'12px', fontWeight:700 }}>
                    {photos.length} staged
                </div>
            </div>
        </div>
    );

    const S = {
        page:    { minHeight:'100vh', background:C.white, color:C.ink, fontFamily:'-apple-system, BlinkMacSystemFont, sans-serif', maxWidth:'1100px', margin:'0 auto' },
        header:  { background:C.ink, padding:'20px 18px', position:'sticky', top:0, zIndex:100 },
        tabBar:  { display:'flex', gap:8, padding:'12px 16px', background:C.white, borderBottom:`1px solid ${C.line}`, position:'sticky', top:84, zIndex:99 },
        tabBtn:  (active) => ({ flex:'0 0 auto', padding:'9px 18px', borderRadius:8, fontSize:14, fontWeight:700, cursor:'pointer', border:`1.5px solid ${active ? C.ink : C.line}`, background: active ? C.ink : C.white, color: active ? '#fff' : C.ink }),
        uploadWrap: { maxWidth:'430px', margin:'0 auto' },
        body:    { padding:'16px' },
        label:   { fontSize:'11px', color:C.gray, fontWeight:700, textTransform:'uppercase', letterSpacing:'0.5px', marginBottom:'6px' },
        input:   { width:'100%', padding:'14px 12px', background:'#f9f9f9', border:`1px solid ${C.line}`, borderRadius:'8px', color:'#111', fontSize:'17px', outline:'none', boxSizing:'border-box' },
        priBtn:  { width:'100%', padding:'16px', background:C.ink, color:'#fff', border:'none', borderRadius:10, fontSize:16, fontWeight:700, cursor:'pointer' },
        summaryBody: { padding:'16px' },
        quickBtn: (active) => ({ padding:'8px 14px', borderRadius:8, fontSize:12.5, fontWeight:600, cursor:'pointer', border:`1.5px solid ${active ? C.ink : C.line}`, background: active ? C.ink : C.white, color: active ? '#fff' : C.gray, whiteSpace:'nowrap' }),
        card:    { background:C.white, border:`1px solid ${C.line}`, borderRadius:10, overflow:'hidden' },
        scrollWrap: { overflowX:'auto' },
        gridRow: { display:'grid', gridTemplateColumns:'0.5fr 1.1fr 1.4fr 1.4fr 1fr 0.9fr', alignItems:'center', minWidth:680 },
        gridHeader: { background:C.ink, color:'#fff', fontSize:10.5, fontWeight:700, textTransform:'uppercase', letterSpacing:'0.04em' },
        cell:    { padding:'12px 10px', fontSize:13, overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap' },
        emptyState: { padding:48, textAlign:'center', color:C.gray, fontSize:14 },
        downloadBtn: { padding:'10px 18px', borderRadius:8, border:'none', background:C.green, color:'#fff', fontWeight:700, fontSize:13, cursor:'pointer' },
    };

    return (
        <div style={S.page}>

            {toast && (
                <div style={{ position:'fixed', top:14, left:'50%', transform:'translateX(-50%)', background:C.ink, color:'#fff', padding:'10px 20px', borderRadius:20, fontSize:13, zIndex:9999, whiteSpace:'nowrap', boxShadow:'0 4px 12px rgba(0,0,0,0.25)' }}>
                    {toast}
                </div>
            )}

            <div style={S.header}>
                <div style={{ display:'flex', alignItems:'center', justifyContent:'space-between' }}>
                    <div>
                        <div style={{ fontSize:'10px', color:C.red, letterSpacing:'2px', textTransform:'uppercase', fontWeight:800 }}>Soulflower</div>
                        <div style={{ fontSize:'20px', fontWeight:800, color:'#fff' }}>Hi{user?.display_name ? `, ${user.display_name}` : ''}!</div>
                        <div style={{ fontSize:'13px', color:'#ccc', marginTop:2 }}>Proof Of Delivery</div>
                    </div>
                    <div style={{ display:'flex', flexDirection:'column', alignItems:'flex-end', gap:8 }}>
                        <button onClick={() => startTour('pod_upload')} style={{ background:'none', border:'1px solid #555', color:'#ccc', fontSize:'11px', padding:'4px 10px', borderRadius:12, cursor:'pointer' }}>🎓 Tutorial</button>
                        {onLogout && (
                            <button onClick={onLogout} style={{ background:'none', border:'none', color:'#ccc', fontSize:'12px', cursor:'pointer' }}>Log out</button>
                        )}
                    </div>
                </div>
            </div>

            <div style={S.tabBar}>
                <button style={S.tabBtn(tab === 'upload')} onClick={() => setTab('upload')}>Upload POD</button>
                <button style={S.tabBtn(tab === 'summary')} onClick={() => setTab('summary')}>Summary</button>
            </div>

            {tab === 'upload' ? (
                <div style={S.uploadWrap}>
                <div style={S.body} data-tutorial="pod-upload-form">

                {/* Invoice number */}
                <div style={{ marginBottom:20 }}>
                    <div style={S.label}>Invoice Number</div>

                    {!manualMode ? (
                        <>
                            <div style={{ display:'flex', gap:6, marginBottom:8 }}>
                                {PREFIXES.map(p => (
                                    <button key={p} onClick={() => setPrefix(p)}
                                        style={{
                                            padding:'8px 12px', borderRadius:8, fontSize:13, fontWeight:700, cursor:'pointer',
                                            border:`1.5px solid ${prefix === p ? C.ink : C.line}`,
                                            background: prefix === p ? C.ink : C.white,
                                            color: prefix === p ? '#fff' : C.ink,
                                        }}>
                                        {p}
                                    </button>
                                ))}
                                <button onClick={() => setManualMode(true)}
                                    style={{ padding:'8px 12px', borderRadius:8, fontSize:13, fontWeight:600, cursor:'pointer', border:`1.5px dashed ${C.gray}`, background:C.white, color:C.gray }}>
                                    Type manually
                                </button>
                            </div>
                            <div style={{ display:'flex', alignItems:'center', gap:0 }}>
                                <div style={{ padding:'14px 8px 14px 12px', background:'#f0f0f0', border:`1px solid ${C.line}`, borderRight:'none', borderRadius:'8px 0 0 8px', fontSize:16, fontFamily:'monospace', color:C.gray }}>
                                    {prefix}
                                </div>
                                <input
                                    type="text"
                                    inputMode="text"
                                    placeholder="1234"
                                    value={invoiceRest}
                                    onChange={(e) => { setInvoiceRest(e.target.value); setError(''); }}
                                    style={{ ...S.input, borderRadius:'0 8px 8px 0', fontFamily:'monospace' }}
                                    autoComplete="off"
                                />
                            </div>
                        </>
                    ) : (
                        <>
                            <input
                                type="text"
                                placeholder="e.g. 26-27/PT1113"
                                value={manualInvoice}
                                onChange={(e) => { setManualInvoice(e.target.value); setError(''); }}
                                style={{ ...S.input, fontFamily:'monospace', marginBottom:8 }}
                                autoComplete="off"
                                autoCapitalize="none"
                            />
                            <button onClick={() => { setManualMode(false); setManualInvoice(''); }}
                                style={{ background:'none', border:'none', color:C.gray, fontSize:12, cursor:'pointer', padding:0, textDecoration:'underline' }}>
                                ← Use prefix picker instead
                            </button>
                        </>
                    )}

                    {/* Typeahead suggestions */}
                    {suggestions.length > 0 && (
                        <div style={{ marginTop:8, border:`1px solid ${C.line}`, borderRadius:8, overflow:'hidden' }}>
                            {suggestions.map(s => (
                                <div key={s.invoice_no} onClick={() => pickSuggestion(s)}
                                    style={{ padding:'10px 12px', borderBottom:`1px solid ${C.line}`, cursor:'pointer', background:C.white }}>
                                    <div style={{ fontSize:13, fontWeight:700, fontFamily:'monospace' }}>{s.invoice_no}</div>
                                    <div style={{ fontSize:11.5, color:C.gray }}>{s.buyer} · PO {s.order_ref_no}</div>
                                </div>
                            ))}
                        </div>
                    )}

                    {/* Confirmed match chip */}
                    {matched && (
                        <div style={{ marginTop:8, padding:'10px 12px', background:C.greenSoft, border:`1px solid ${C.green}`, borderRadius:8 }}>
                            <div style={{ fontSize:12.5, fontWeight:700, color:C.green }}>✓ {matched.invoice_no}</div>
                            <div style={{ fontSize:11.5, color:C.gray }}>{matched.buyer} · PO {matched.order_ref_no}</div>
                        </div>
                    )}
                </div>

                {/* Docket number (optional) */}
                <div style={{ marginBottom:20 }}>
                    <div style={S.label}>Docket Number (optional)</div>
                    <input
                        type="text"
                        placeholder="e.g. 693"
                        value={docketNo}
                        onChange={(e) => setDocketNo(e.target.value)}
                        style={S.input}
                        autoComplete="off"
                    />
                </div>

                {error && (
                    <div style={{ background:'#fef2f2', border:`1px solid #fca5a5`, borderRadius:8, padding:'10px 12px', color:'#dc2626', fontSize:13, marginBottom:16 }}>
                        {error}
                    </div>
                )}

                {/* Photos / documents */}
                <div style={{ marginBottom:20 }}>
                    <div style={S.label}>Delivery Photos / Documents {photos.length > 0 && `(${photos.length})`}</div>
                    <div style={{ display:'flex', flexWrap:'wrap', gap:8 }}>
                        <button onClick={openCamera} disabled={!invoiceValue} title="Take photo"
                            style={{ width:80, height:80, background: invoiceValue ? C.panel : '#eee', border:`2px dashed ${invoiceValue ? C.ink : C.line}`, borderRadius:8, display:'flex', alignItems:'center', justifyContent:'center', fontSize:26, cursor: invoiceValue ? 'pointer' : 'not-allowed', flexShrink:0, color:C.ink }}>
                            📷
                        </button>
                        <button onClick={() => deviceFileInputRef.current?.click()} disabled={!invoiceValue} title="Upload from device"
                            style={{ width:80, height:80, background: invoiceValue ? C.panel : '#eee', border:`2px dashed ${invoiceValue ? C.ink : C.line}`, borderRadius:8, display:'flex', alignItems:'center', justifyContent:'center', fontSize:26, cursor: invoiceValue ? 'pointer' : 'not-allowed', flexShrink:0, color:C.ink }}>
                            📁
                        </button>
                        <input
                            ref={fileInputRef}
                            type="file"
                            accept="image/*"
                            capture="environment"
                            multiple
                            onChange={handleFileInput}
                            style={{ display:'none' }}
                        />
                        {/* No `capture` attribute — opens the normal gallery/files
                            picker instead of forcing the camera. */}
                        <input
                            ref={deviceFileInputRef}
                            type="file"
                            accept="image/jpeg,image/png,application/pdf"
                            multiple
                            onChange={handleDeviceFileInput}
                            style={{ display:'none' }}
                        />
                        {photos.map((p, i) => (
                            <div key={i} style={{ position:'relative', flexShrink:0 }}>
                                {p.kind === 'pdf' ? (
                                    <div style={{ width:80, height:80, borderRadius:8, border:`2px solid ${C.line}`, background:C.panel, display:'flex', flexDirection:'column', alignItems:'center', justifyContent:'center', gap:4, padding:4, boxSizing:'border-box' }}>
                                        <span style={{ fontSize:22 }}>📄</span>
                                        <span style={{ fontSize:9, color:C.gray, textAlign:'center', wordBreak:'break-all', lineHeight:1.1, maxHeight:22, overflow:'hidden' }}>{p.name}</span>
                                    </div>
                                ) : (
                                    <img src={p.preview} alt="" style={{ width:80, height:80, objectFit:'cover', borderRadius:8, border:`2px solid ${C.line}`, display:'block' }} />
                                )}
                                <div onClick={() => removePhoto(i)}
                                    style={{ position:'absolute', top:-7, right:-7, background:C.red, color:'#fff', borderRadius:'50%', width:20, height:20, fontSize:11, display:'flex', alignItems:'center', justifyContent:'center', cursor:'pointer', fontWeight:700, boxShadow:'0 1px 4px rgba(0,0,0,0.3)' }}>
                                    ✕
                                </div>
                            </div>
                        ))}
                    </div>
                </div>

                {/* Save & Confirm */}
                <button
                    style={{ ...S.priBtn, background: (saving || !invoiceValue || photos.length === 0) ? '#aaa' : C.ink, cursor: (saving || !invoiceValue || photos.length === 0) ? 'not-allowed' : 'pointer' }}
                    onClick={handleSave}
                    disabled={saving || !invoiceValue || photos.length === 0}
                >
                    {saving ? '⏳ Saving…' : '✓ Save & Confirm'}
                </button>

                </div>
                </div>
            ) : (
                <div style={S.summaryBody} data-tutorial="pod-summary">
                    <div style={{ display:'flex', flexWrap:'wrap', gap:8, alignItems:'center', marginBottom:14 }}>
                        {QUICK_RANGES.map(q => (
                            <button key={q.key} style={S.quickBtn(quickRange === q.key)} onClick={() => applyQuickRange(q.key)}>
                                {q.label}
                            </button>
                        ))}
                        <div style={{ flex:1, minWidth:12 }} />
                        <input type="date" value={fromDate} onChange={e => onManualDateChange(setFromDate, e.target.value)} style={{ ...S.input, width:'auto', padding:'8px 10px', fontSize:13 }} />
                        <span style={{ color:C.gray, fontSize:13 }}>to</span>
                        <input type="date" value={toDate} onChange={e => onManualDateChange(setToDate, e.target.value)} style={{ ...S.input, width:'auto', padding:'8px 10px', fontSize:13 }} />
                        <button style={S.downloadBtn} onClick={downloadSummaryExcel} disabled={summaryRows.length === 0}>
                            ⬇ Download Excel
                        </button>
                    </div>

                    {summaryLoading ? (
                        <p style={S.emptyState}>Loading…</p>
                    ) : summaryError ? (
                        <p style={{ ...S.emptyState, color:C.red }}>Failed to load: {summaryError}</p>
                    ) : summaryRows.length === 0 ? (
                        <p style={S.emptyState}>No PODs uploaded in this range.</p>
                    ) : (
                        <div style={S.card}>
                            <div style={S.scrollWrap}>
                                <div style={{ ...S.gridRow, ...S.gridHeader }}>
                                    <div style={S.cell}>#</div>
                                    <div style={S.cell}>POD Date</div>
                                    <div style={S.cell}>Uploaded Date</div>
                                    <div style={S.cell}>Invoice No.</div>
                                    <div style={S.cell}>Docket No.</div>
                                    <div style={S.cell}>Photos</div>
                                </div>
                                {summaryRows.map((r, idx) => (
                                    <div key={r.id} style={{ ...S.gridRow, borderTop:`1px solid ${C.line}`, background: idx % 2 ? C.panel : C.white }}>
                                        <div style={{ ...S.cell, color:C.gray }}>{idx + 1}</div>
                                        <div style={S.cell}>{formatDate(r.uploading_date)}</div>
                                        <div style={S.cell}>{formatDateTime(r.created_at)}</div>
                                        <div style={{ ...S.cell, fontWeight:700 }}>{r.invoice_number}</div>
                                        <div style={S.cell}>{r.docket_no || '—'}</div>
                                        <div style={S.cell}>
                                            <button onClick={() => openPod(r)} disabled={!r.view_url}
                                                style={{ background:'none', border:'none', color: r.view_url ? C.ink : C.gray, fontWeight:700, fontSize:13, textDecoration: r.view_url ? 'underline' : 'none', cursor: r.view_url ? 'pointer' : 'not-allowed', padding:0 }}>
                                                {r.view_url ? '📷 View' : 'Unavailable'}
                                            </button>
                                        </div>
                                    </div>
                                ))}
                            </div>
                        </div>
                    )}
                </div>
            )}
        </div>
    );
}
